import {
  Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, UntypedFormControl } from '@angular/forms';
import * as _ from 'lodash';



import { nestedTableEditableColumns } from './editable-columns';
import { FitmentsService } from 'shared/fitments/fitments.service';
import {takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'cell-edit-form',
  templateUrl: 'cell-edit-form.component.html',
  styleUrls: ['cell-edit-form.component.scss'],
})
export class CellEditFormComponent implements OnChanges, OnDestroy {
  @Input() row: any;
  @Input() column: any;
  @Input() index: number;
  @Input() brandId: string;
  @Input() productId: string;
  @Input() editMode: boolean;
  @Input() isComingSoon: boolean;

  @Output() cancelClick: EventEmitter<any> = new EventEmitter<any>();
  @Output() saveFinished: EventEmitter<any> = new EventEmitter<any>();

  form: UntypedFormGroup;
  editableColumns: any = nestedTableEditableColumns;
  controlsIsReady: boolean = false;
  saveInProgress: boolean = false;
  isActive: boolean = true;

  get columnName(): string {
    return _.get(this.column, 'columnName');
  }

  get columnTitle(): string {
    return _.get(this.column, 'tooltipTitle');
  }

  get columnFieldMap(): string {
    return _.get(this.editableColumns, `${this.columnName}.fieldMap`);
  }

  get columnItemName(): any {
    return _.get(this.editableColumns, `${this.columnName}.itemName`);
  }

  get columnControlType(): string {
    return _.get(this.editableColumns, `${this.columnName}.controlType`);
  }

  get isColumnFormArray(): string {
    return _.get(this.editableColumns, `${this.columnName}.formArray`);
  }

  get columnEditData(): any {
    return _.get(this.editableColumns, this.columnName);
  }

  get columnFormGroup(): UntypedFormGroup {
    return _.get(this.form, `controls.${this.columnName}`);
  }

  // can be FormControl|FormArray
  get columnFormControl(): any {
    return _.get(this.form, `controls.${this.columnName}.controls.${this.columnFieldMap}`);
  }

  get columnValue(): any {
    return _.get(this.row, this.columnItemName);
  }

  get appId(): string|number {
    return _.get(this.row, 'assignment_values.app_id');
  }

  get isNestedArray(): boolean {
    return _.get(this.editableColumns, `${this.columnName}.nestedArray`);
  }

  get partTypesValidation(): Array<any> {
    return _.get(this.row, 'validation.part_terminologies');
  }

  get qualifiersValidation(): Array<any> {
    return _.get(this.row, 'validation.qualifiers');
  }

  constructor(
    private fb: UntypedFormBuilder,
    private ref: ChangeDetectorRef,
    private fitmentsService: FitmentsService,
  ) {
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.column && changes.column.currentValue && !this.isComingSoon) {
      this.controlsIsReady = false;
      this.populateControl();
      _.defer(() => {
        this.controlsIsReady = true;
        this.ref.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.isActive = false;
  }

  onCancelClick() {
    this.cancelClick.emit();
  }

  onSaveClick() {
    this.saveInProgress = true;
    const requestData = this.getDataForSave();
    this.fitmentsService.updateApps(this.brandId, this.productId, requestData)
    .pipe(takeWhile(() => this.isActive))
    .subscribe((res: any) => {
      this.saveFinished.emit({
        appId: _.get(res, 'app_ids[0]'),
        item_detail_revision_id: _.get(res, 'item_detail_revision_id'),
      });
    }, () => {
      this.saveInProgress = false;
    });
  }

  onDeleteControlClick(index: number) {
    this.columnFormControl.removeAt(index);
  }

  onAddControlClick() {
    this.columnFormControl.push(this.createFormArrayItem());
  }

  /*
    * Creates form empty structure
   */
  private createForm(): void {
    const groups: any = _.reduce(this.editableColumns, (result: any, value: any, key: string) => {
      if (value.comingSoon) { return result; }
      let control: UntypedFormControl|UntypedFormArray;
      if (value.formArray) {
        control = new UntypedFormArray([]);
      } else {
        control = new UntypedFormControl('');
      }
      result[key] = this.fb.group({
        [value.fieldMap]: control,
      });
      return result;
    }, {});
    this.form = this.fb.group(groups);
  }

  /*
    * Populate column control with row data
   */
  private populateControl(): void {
    if (this.isColumnFormArray) {
      this.columnFormGroup.setControl(this.columnFieldMap, new UntypedFormArray([]));
      _.each(this.columnValue, (item: any) => {
        this.columnFormControl.push(this.createFormArrayItem(item));
      });
      if (!this.columnFormControl.controls.length) {
        this.columnFormControl.push(this.createFormArrayItem());
      }
    } else {
      this.columnFormControl.setValue(this.columnValue);
    }
  }

  private createFormArrayItem(dataItem?: any): UntypedFormGroup {
    return this.fb.group(_.reduce(this.columnEditData.controls, (result: any, item: any) => {
      result[item.fieldMap] = new UntypedFormControl(_.get(dataItem, item.itemName), this.getValidator(dataItem, item.itemName));
      return result;
    }, {}));
  }

  private getDataForSave(): any {
    const data: any = {
      app_ids: [this.appId],
      qty: _.get(this.row, 'assignment_values.qty'),
      position_id: _.get(this.row, 'assignment_values.position_id'),
    };
    _.each(this.editableColumns, (item: any, columnName: string) => {
      if (item.comingSoon) { return; }
      if (this.columnName === columnName) {
        _.set(data, item.fieldMap, this.columnFormControl.value);
      } else {
        _.set(data, item.fieldMap, _.get(this.row, item.itemName));
      }
    });
    data.mfr_label = _.get(data, 'mfr_label', '').trim();
    _.remove(data.notes, (note: any) => !note.value);
    return data;
  }

  private getValidator(dataItem, itemName) {
    switch (this.columnName) {
      case 'partTerminologies':
        const validation = this.partTypesValidation;
        const validationItem = validation?.find(item => item.value === dataItem.id);
        if (!validationItem || validationItem?.combined_fields?.invalid_field === 'id') {
          return null;
        }
        // validation for position ID on position field
        if (validationItem?.combined_fields?.invalid_field === 'position_id' && itemName === 'position') {
          return () => new Error(validationItem?.message);
        }
        if (validationItem?.combined_fields?.invalid_field !== itemName) {
          return null;
        }
        return () => new Error(validationItem?.message);
      case 'qualifiers':
        // const qValidation = this.qualifiersValidation;
        // const qValidationItem = qValidation?.find(item => item.value === dataItem.id);
        // return qValidationItem ? () => new Error(qValidationItem?.message) : null;
      // falls through
      default:
        return null;
    }
  }

  getIsInvalidRow(formGroup) {
    switch (this.columnName) {
      case 'partTerminologies': {
        const rowId: number = formGroup?.value?.id;
        const validation = this.partTypesValidation;

        return validation?.find(item => item.value === rowId && item?.combined_fields?.invalid_field === 'id');
      }
      case 'qualifiers': {
        const rowId: number = formGroup?.value?.id;
        const validation = this.qualifiersValidation;

        return validation?.find(item => item.value === rowId);
      }
      default:
        return false;
    }
  }

  // todo remove after part types edit implementing
  get isEditMode() {
    return this.editMode && this.columnName !== 'partTerminologies' && this.columnName !== 'qualifiers';
  }

  getTemplate(formGroup, fieldMap) {
    if (this.columnName !== 'qualifiers') {
      return null;
    }
    if (fieldMap !== 'text_format') {
      return null;
    }
    const value = this.columnValue?.find(item => item.id === formGroup?.value?.id);
    let textFormat: string = value?.text_format;
    const paramStringArray: string[] | undefined = value?.param;
    if (typeof paramStringArray === 'undefined') {
      return textFormat;
    }
    paramStringArray.map((param, index) => {
      const startIndex = textFormat.indexOf(`<p${index + 1}`);
      const endIndex = textFormat.indexOf('/>') + 2;
      const template = textFormat.slice(startIndex, endIndex);
      textFormat = textFormat.replace(template, `<span class="template-string">${param}</span>`);
    });
    return textFormat;
  }
}
